import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, MapPin, Calendar, Flag, Users, 
  FileText, Upload, CheckCircle, CheckCircle2, XCircle, Clock, AlertCircle,
  Lock, Eye, EyeOff, Save, Briefcase
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default document types for KYC (will be filtered by admin settings)
const ALL_KYC_DOCUMENT_TYPES = [
  { value: 'identity_front', label: "Pièce d'identité — Recto", group: 'identity' },
  { value: 'identity_back', label: "Pièce d'identité — Verso", group: 'identity' },
  { value: 'proof_address', label: "Justificatif de domicile" },
  { value: 'proof_income', label: "Justificatif de revenus" },
  { value: 'bank_statement', label: "Relevé bancaire" },
  { value: 'iban', label: "IBAN / RIB" },
  { value: 'tax_return', label: "Avis d'imposition" },
  { value: 'other', label: "Autre document" }
];

export default function ClientProfile() {
  const { token, user } = useAuth();
  const { currentTemplate } = useTheme();
  const { t } = useLanguage();
  
  const [profile, setProfile] = useState(null);
  const [kycDocs, setKycDocs] = useState([]);
  const [kycRequiredDocs, setKycRequiredDocs] = useState(null); // null = loading, [] = no documents required
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKyc, setUploadingKyc] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);
  
  // Form states for profile changes
  const [profileChanges, setProfileChanges] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmBeneficiary, setConfirmBeneficiary] = useState(false);
  
  // Password change
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  
  // PIN change
  const [pinData, setPinData] = useState({
    current_pin: '',
    new_pin: '',
    confirm_pin: ''
  });
  
  // KYC upload
  const [kycUpload, setKycUpload] = useState({
    document_type: 'identity_front',
    file: null
  });
  const fileInputRef = useRef(null);
  
  // Get available KYC document types based on admin settings
  // If kycRequiredDocs is null/undefined (not yet loaded from server), show all types as fallback
  // If kycRequiredDocs is an empty array (admin explicitly set no requirements), show no types
  const availableKycTypes = (kycRequiredDocs && kycRequiredDocs.length > 0)
    ? ALL_KYC_DOCUMENT_TYPES.filter(docType => 
        kycRequiredDocs.some(req => req.type === docType.value)
      )
    : [];
  
  const primaryColor = currentTemplate?.colors?.primary || '#0052FF';
  const accentColor = currentTemplate?.colors?.accent || '#10B981';

  useEffect(() => {
    fetchData();
  }, [token, user]);

  const fetchData = async () => {
    try {
      const [profileRes, kycRes, kycRequirementsRes] = await Promise.all([
        axios.get(`${API}/clients/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/kyc/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        // Fetch client-specific requirements (falls back to global if not customized)
        axios.get(`${API}/kyc-requirements/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { documents: [] } }))
      ]);
      setProfile(profileRes.data);
      setKycDocs(kycRes.data || []);
      setKycRequiredDocs(kycRequirementsRes.data?.documents || []);
      
      // Set default document type to first available type
      const requiredDocs = kycRequirementsRes.data?.documents || [];
      if (requiredDocs.length > 0) {
        setKycUpload(prev => ({ ...prev, document_type: requiredDocs[0].type }));
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileChanges(prev => ({ ...prev, [field]: value }));
    setProfile(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSubmitChanges = async () => {
    if (!hasChanges || Object.keys(profileChanges).length === 0) {
      toast.info('Aucune modification à soumettre');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/clients/${user.id}/request-changes`, profileChanges, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Modifications soumises pour approbation');
      setHasChanges(false);
      setProfileChanges({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordDialog(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const handlePinChange = async () => {
    if (pinData.new_pin !== pinData.confirm_pin) {
      toast.error('Les codes PIN ne correspondent pas');
      return;
    }
    if (pinData.new_pin.length !== 6 || !/^\d+$/.test(pinData.new_pin)) {
      toast.error('Le code PIN doit contenir exactement 6 chiffres');
      return;
    }
    
    setSaving(true);
    try {
      // First verify current PIN if exists
      if (user?.has_pin) {
        await axios.post(`${API}/auth/verify-pin`, { pin: pinData.current_pin }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      // Then set new PIN
      await axios.post(`${API}/auth/setup-pin`, { pin: pinData.new_pin }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Code PIN modifié avec succès');
      // Update user in localStorage to reflect has_pin
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        stored.has_pin = true;
        localStorage.setItem('user', JSON.stringify(stored));
      } catch {}
      setShowPinDialog(false);
      setPinData({ current_pin: '', new_pin: '', confirm_pin: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de PIN');
    } finally {
      setSaving(false);
    }
  };

  const handleKycUpload = async () => {
    if (!kycUpload.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    setUploadingKyc(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        await axios.post(`${API}/kyc/upload`, {
          document_type: kycUpload.document_type,
          file_url: base64,
          file_name: kycUpload.file.name
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Document téléversé avec succès');
        setKycUpload({ document_type: 'identity', file: null });
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchData();
        setUploadingKyc(false);
      };
      reader.readAsDataURL(kycUpload.file);
    } catch (error) {
      toast.error('Erreur lors du téléversement');
      setUploadingKyc(false);
    }
  };

  const getKycStatusBadge = (status) => {
    const config = {
      pending: { className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50', icon: Clock, label: 'En attente' },
      under_review: { className: 'bg-blue-500/20 text-blue-500 border-blue-500/50', icon: Clock, label: 'En révision' },
      approved: { className: 'bg-green-500/20 text-green-500 border-green-500/50', icon: CheckCircle, label: 'Validé' },
      rejected: { className: 'bg-red-500/20 text-red-500 border-red-500/50', icon: XCircle, label: 'Refusé' }
    };
    const { className, icon: Icon, label } = config[status] || config.pending;
    return (
      <Badge variant="outline" className={className}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingStatus = profile?.pending_changes_status;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-profile">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Mon Profil</h1>
            <p className="text-muted-foreground">Code client: {profile?.client_code}</p>
          </div>
          {pendingStatus === 'pending' && (
            <Badge className="bg-yellow-500/20 text-yellow-500">
              <Clock className="w-4 h-4 mr-1" />
              Modifications en attente d'approbation
            </Badge>
          )}
          {pendingStatus === 'approved' && (
            <Badge className="bg-green-500/20 text-green-500">
              <CheckCircle className="w-4 h-4 mr-1" />
              Modifications approuvées
            </Badge>
          )}
        </div>

        {/* Company Info Banner (Professional accounts) */}
        {profile?.account_type === 'professionnel' && (
          <div className="p-4 rounded-lg border border-amber-500/20 bg-amber-500/5 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-amber-500" />
              <span className="font-semibold text-sm">{profile?.company_name || 'Compte Professionnel'}</span>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-amber-500/15 text-amber-500 border border-amber-500/30">PRO</span>
            </div>
            {profile?.company_siret && (
              <div className="text-xs text-muted-foreground">SIRET : <span className="font-mono text-foreground">{profile.company_siret}</span></div>
            )}
            {profile?.company_legal_form && (
              <div className="text-xs text-muted-foreground">Forme : <span className="text-foreground">{profile.company_legal_form}</span></div>
            )}
            {profile?.company_activity && (
              <div className="text-xs text-muted-foreground">Activité : <span className="text-foreground">{profile.company_activity}</span></div>
            )}
            {(profile?.company_address || profile?.company_city) && (
              <div className="text-xs text-muted-foreground">
                {[profile?.company_address, profile?.company_postal_code, profile?.company_city].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-card/80 backdrop-blur">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Informations personnelles
              </CardTitle>
              <CardDescription>
                Les modifications sont soumises pour approbation par votre conseiller
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input value={user?.first_name || ''} disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input value={user?.last_name || ''} disabled className="bg-muted" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Email
                </Label>
                <Input value={user?.email || ''} disabled className="bg-muted" />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Téléphone principal
                  </Label>
                  <Input 
                    value={profile?.phone_primary || user?.phone || ''}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone secondaire</Label>
                  <Input 
                    value={profile?.phone_secondary || ''}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Date de naissance
                  </Label>
                  <Input 
                    type="date"
                    value={profileChanges.date_of_birth ?? profile?.date_of_birth ?? ''}
                    onChange={(e) => handleProfileChange('date_of_birth', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lieu de naissance</Label>
                  <Input 
                    value={profileChanges.place_of_birth ?? profile?.place_of_birth ?? ''}
                    onChange={(e) => handleProfileChange('place_of_birth', e.target.value)}
                    placeholder="Paris"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Flag className="w-4 h-4" /> Nationalité
                </Label>
                <Input 
                  value={profileChanges.nationality ?? profile?.nationality ?? ''}
                  onChange={(e) => handleProfileChange('nationality', e.target.value)}
                  placeholder="Française"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Adresse
                </Label>
                <Input 
                  value={profileChanges.address ?? profile?.address ?? ''}
                  onChange={(e) => handleProfileChange('address', e.target.value)}
                  placeholder="123 Rue Exemple"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input 
                    value={profileChanges.postal_code ?? profile?.postal_code ?? ''}
                    onChange={(e) => handleProfileChange('postal_code', e.target.value)}
                    placeholder="75001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input 
                    value={profileChanges.city ?? profile?.city ?? ''}
                    onChange={(e) => handleProfileChange('city', e.target.value)}
                    placeholder="Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input 
                    value={profileChanges.country ?? profile?.country ?? ''}
                    onChange={(e) => handleProfileChange('country', e.target.value)}
                    placeholder="France"
                  />
                </div>
              </div>
              
              {hasChanges && (
                <Button 
                  onClick={handleSubmitChanges}
                  disabled={saving || pendingStatus === 'pending'}
                  className="w-full mt-4"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Envoi...' : 'Soumettre les modifications'}
                </Button>
              )}
              
              {pendingStatus === 'pending' && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-sm text-yellow-500 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Vos modifications précédentes sont en attente d'approbation
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Beneficiary & Security */}
          <div className="space-y-6">
            {/* Beneficiaries */}
            <Card className="bg-card/80 backdrop-blur">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Successeurs / Bénéficiaires
                  </CardTitle>
                  <Badge variant="outline" className="font-mono">
                    {(profile?.beneficiaries || []).reduce((s, b) => s + (b.percentage || 0), 0)}% / 100%
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {(profile?.beneficiaries || []).map((b, idx) => {
                  const isLocked = b.locked;
                  const hasPhone = !!b.phone;
                  const hasId = b.id_type && (b.id_front_url || b.passport_url);
                  const isComplete = hasPhone && hasId;
                  const idStatus = b.id_status || 'none';
                  return (
                    <div key={idx} className={`p-4 rounded-lg border space-y-3 ${isLocked ? 'border-primary/20 bg-primary/5' : 'bg-muted/20'}`}>
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isLocked ? <Lock className="w-3.5 h-3.5 text-primary" /> : <Clock className="w-3.5 h-3.5 text-amber-500" />}
                          <span className="text-sm font-semibold">{b.first_name} {b.last_name}</span>
                          <Badge className="text-[10px]" variant="outline">{b.percentage}%</Badge>
                          {isComplete && idStatus === 'approved' && <Badge className="text-[10px] bg-green-600">Validé</Badge>}
                          {isComplete && idStatus === 'pending' && <Badge className="text-[10px] bg-amber-500 text-black">En attente</Badge>}
                          {idStatus === 'rejected' && <Badge className="text-[10px] bg-red-500">Refusé — renvoyez vos documents</Badge>}
                          {!isComplete && isLocked && idStatus !== 'rejected' && <Badge className="text-[10px] bg-amber-500/20 text-amber-500">Documents manquants</Badge>}
                        </div>
                        {!isLocked && (
                          <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => {
                            const updated = [...(profile?.beneficiaries || [])];
                            updated.splice(idx, 1);
                            handleProfileChange('beneficiaries', updated);
                          }}>Retirer</Button>
                        )}
                      </div>

                      {/* Info row */}
                      <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
                        {b.relation && <span>Relation : {b.relation}</span>}
                        {b.date_of_birth && <span>Né(e) le : {b.date_of_birth}</span>}
                        {b.phone && <span>Tél : {b.phone}</span>}
                      </div>

                      {/* Editable fields (new beneficiary not locked) */}
                      {!isLocked && (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Nom *" value={b.last_name || ''} onChange={(e) => {
                              const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], last_name: e.target.value}; handleProfileChange('beneficiaries', updated);
                            }} />
                            <Input placeholder="Prénom *" value={b.first_name || ''} onChange={(e) => {
                              const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], first_name: e.target.value}; handleProfileChange('beneficiaries', updated);
                            }} />
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <Input type="date" value={b.date_of_birth || ''} onChange={(e) => {
                              const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], date_of_birth: e.target.value}; handleProfileChange('beneficiaries', updated);
                            }} />
                            <Input placeholder="Relation" value={b.relation || ''} onChange={(e) => {
                              const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], relation: e.target.value}; handleProfileChange('beneficiaries', updated);
                            }} />
                            <Input placeholder="Téléphone" value={b.phone || ''} onChange={(e) => {
                              const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], phone: e.target.value}; handleProfileChange('beneficiaries', updated);
                            }} />
                            <div className="flex items-center gap-1">
                              <Input type="number" min={0} max={100} value={b.percentage || ''} placeholder="%" onChange={(e) => {
                                const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], percentage: val};
                                if (updated.reduce((s, x) => s + (x.percentage || 0), 0) > 100) return;
                                handleProfileChange('beneficiaries', updated);
                              }} />
                              <span className="text-sm">%</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* ID Document section */}
                      {(isLocked || b.first_name) && (
                        <div className="pt-2 border-t border-border/30 space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground">Pièce d'identité du bénéficiaire</p>
                          {(() => {
                            const hasAllDocs = b.id_type === 'passport' ? !!b.passport_url : (b.id_type === 'ci' ? (!!b.id_front_url && !!b.id_back_url) : false);
                            const showUpload = !hasAllDocs && idStatus !== 'pending' && idStatus !== 'approved';
                            return showUpload ? (
                            <>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => { const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], id_type: 'ci'}; handleProfileChange('beneficiaries', updated); }}
                                  className={`flex-1 p-3 rounded-lg border text-center transition-all ${b.id_type === 'ci' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-muted-foreground'}`}
                                >
                                  <FileText className="w-5 h-5 mx-auto mb-1" />
                                  <p className="text-xs font-semibold">Carte d'identité</p>
                                  <p className="text-[10px] text-muted-foreground">Recto + Verso</p>
                                </button>
                                <button
                                  onClick={() => { const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], id_type: 'passport'}; handleProfileChange('beneficiaries', updated); }}
                                  className={`flex-1 p-3 rounded-lg border text-center transition-all ${b.id_type === 'passport' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-muted-foreground'}`}
                                >
                                  <FileText className="w-5 h-5 mx-auto mb-1" />
                                  <p className="text-xs font-semibold">Passeport</p>
                                  <p className="text-[10px] text-muted-foreground">1 document</p>
                                </button>
                              </div>
                              {b.id_type === 'ci' && (
                                <div className="grid grid-cols-2 gap-3">
                                  <label className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all ${b.id_front_url ? 'border-green-500 bg-green-500/5' : 'border-border hover:border-primary'}`}>
                                    <Upload className={`w-5 h-5 mb-1 ${b.id_front_url ? 'text-green-500' : 'text-muted-foreground'}`} />
                                    <span className="text-xs font-semibold">{b.id_front_url ? 'Recto envoyé' : 'Recto'}</span>
                                    <span className="text-[10px] text-muted-foreground">JPG, PNG ou PDF</span>
                                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => {
                                      const file = e.target.files?.[0]; if (!file) return;
                                      try {
                                        const fd = new FormData(); fd.append('file', file);
                                        const res = await axios.post(`${API}/upload-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                                        const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], id_front_url: res.data.url}; handleProfileChange('beneficiaries', updated);
                                        toast.success('Recto envoyé');
                                      } catch { toast.error('Erreur envoi'); }
                                    }} />
                                  </label>
                                  <label className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all ${b.id_back_url ? 'border-green-500 bg-green-500/5' : 'border-border hover:border-primary'}`}>
                                    <Upload className={`w-5 h-5 mb-1 ${b.id_back_url ? 'text-green-500' : 'text-muted-foreground'}`} />
                                    <span className="text-xs font-semibold">{b.id_back_url ? 'Verso envoyé' : 'Verso'}</span>
                                    <span className="text-[10px] text-muted-foreground">JPG, PNG ou PDF</span>
                                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => {
                                      const file = e.target.files?.[0]; if (!file) return;
                                      try {
                                        const fd = new FormData(); fd.append('file', file);
                                        const res = await axios.post(`${API}/upload-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                                        const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], id_back_url: res.data.url}; handleProfileChange('beneficiaries', updated);
                                        toast.success('Verso envoyé');
                                      } catch { toast.error('Erreur envoi'); }
                                    }} />
                                  </label>
                                </div>
                              )}
                              {b.id_type === 'passport' && (
                                <label className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all ${b.passport_url ? 'border-green-500 bg-green-500/5' : 'border-border hover:border-primary'}`}>
                                  <Upload className={`w-5 h-5 mb-1 ${b.passport_url ? 'text-green-500' : 'text-muted-foreground'}`} />
                                  <span className="text-xs font-semibold">{b.passport_url ? 'Passeport envoyé' : 'Envoyer le passeport'}</span>
                                  <span className="text-[10px] text-muted-foreground">JPG, PNG ou PDF</span>
                                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => {
                                    const file = e.target.files?.[0]; if (!file) return;
                                    try {
                                      const fd = new FormData(); fd.append('file', file);
                                      const res = await axios.post(`${API}/upload-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                                      const updated = [...(profile?.beneficiaries || [])]; updated[idx] = {...updated[idx], passport_url: res.data.url}; handleProfileChange('beneficiaries', updated);
                                      toast.success('Passeport envoyé');
                                    } catch { toast.error('Erreur envoi'); }
                                  }} />
                                </label>
                              )}
                            </>
                          ) : null; })()}
                          {/* Show uploaded docs status */}
                          {(b.id_front_url || b.passport_url) && (
                            <div className="flex gap-2 flex-wrap items-center">
                              {b.id_front_url && <Badge variant="outline" className="text-[10px] text-green-500 border-green-500">Recto</Badge>}
                              {b.id_back_url && <Badge variant="outline" className="text-[10px] text-green-500 border-green-500">Verso</Badge>}
                              {b.passport_url && <Badge variant="outline" className="text-[10px] text-green-500 border-green-500">Passeport</Badge>}
                              {idStatus === 'pending' && <Badge className="text-[10px] bg-amber-500 text-black">En attente de validation</Badge>}
                              {idStatus === 'approved' && <Badge className="text-[10px] bg-green-600">Validé</Badge>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add button */}
                {(profile?.beneficiaries || []).reduce((s, b) => s + (b.percentage || 0), 0) < 100 && (
                  <Button variant="outline" className="w-full" onClick={() => {
                    const updated = [...(profile?.beneficiaries || []), { first_name: '', last_name: '', date_of_birth: '', relation: '', phone: '', percentage: 0, locked: false, id_type: '', id_status: 'none' }];
                    handleProfileChange('beneficiaries', updated);
                  }} data-testid="add-beneficiary-btn">
                    + Ajouter un bénéficiaire
                  </Button>
                )}

                {/* Save buttons */}
                {hasChanges && profileChanges.beneficiaries && (
                  <div className="flex gap-2">
                    <Button className="flex-1" variant="outline" disabled={saving} onClick={async () => {
                      setSaving(true);
                      try {
                        await axios.post(`${API}/clients/${user.id}/request-changes`, { beneficiaries: profile.beneficiaries }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Bénéficiaires sauvegardés');
                        setHasChanges(false); setProfileChanges({}); fetchData();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                      finally { setSaving(false); }
                    }} data-testid="save-beneficiaries-draft-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Sauvegarder le brouillon
                    </Button>
                    {(profile?.beneficiaries || []).some(b => !b.locked && b.first_name && b.last_name && b.percentage > 0) && (
                      <Button className="flex-1" style={{ backgroundColor: primaryColor }} disabled={saving} onClick={() => {
                        const newOnes = (profile?.beneficiaries || []).filter(b => !b.locked);
                        const total = (profile?.beneficiaries || []).reduce((s, b) => s + (b.percentage || 0), 0);
                        if (total > 100) { toast.error(`Le total dépasse 100% (${total}%)`); return; }
                        const incomplete = newOnes.some(b => !b.first_name || !b.last_name || !b.percentage);
                        if (incomplete) { toast.error('Remplissez le nom, prénom et pourcentage'); return; }
                        setConfirmBeneficiary(true);
                      }} data-testid="save-beneficiaries-btn">
                        Valider définitivement
                      </Button>
                    )}
                  </div>
                )}

                {/* Info */}
                {(profile?.beneficiaries || []).some(b => b.locked) && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Les bénéficiaires validés sont verrouillés. Contactez votre conseiller pour toute modification.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Confirmation Dialog */}
            <Dialog open={confirmBeneficiary} onOpenChange={setConfirmBeneficiary}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Confirmer les bénéficiaires</DialogTitle>
                  <DialogDescription>
                    Une fois validés, vous ne pourrez plus les modifier. Vérifiez les informations.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-3">
                  {(profile?.beneficiaries || []).filter(b => !b.locked).map((b, i) => (
                    <div key={i} className="p-3 rounded-lg border bg-muted/20">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-sm">{b.first_name} {b.last_name}</span>
                        <Badge>{b.percentage}%</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {b.relation && <span>Relation : {b.relation} — </span>}
                        {b.date_of_birth && <span>Né(e) le : {b.date_of_birth} — </span>}
                        {b.phone && <span>Tél : {b.phone}</span>}
                      </div>
                      <div className="flex gap-1 mt-1">
                        {b.id_front_url && <Badge variant="outline" className="text-[10px]">CI Recto</Badge>}
                        {b.id_back_url && <Badge variant="outline" className="text-[10px]">CI Verso</Badge>}
                        {b.passport_url && <Badge variant="outline" className="text-[10px]">Passeport</Badge>}
                      </div>
                    </div>
                  ))}
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-500 font-semibold">Cette action est définitive.</p>
                    <p className="text-xs text-amber-400/80 mt-1">Après validation, seul votre conseiller pourra modifier ces informations.</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmBeneficiary(false)}>Annuler</Button>
                  <Button style={{ backgroundColor: primaryColor }} disabled={saving} onClick={async () => {
                    setSaving(true);
                    try {
                      const updated = (profile?.beneficiaries || []).map(b => {
                        if (b.locked) return b;
                        const locked = { ...b, locked: true };
                        if (locked.id_front_url || locked.passport_url) locked.id_status = 'pending';
                        return locked;
                      });
                      await axios.post(`${API}/clients/${user.id}/request-changes`, { beneficiaries: updated }, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Bénéficiaires validés et verrouillés');
                      setConfirmBeneficiary(false); setHasChanges(false); setProfileChanges({}); fetchData();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                    finally { setSaving(false); }
                  }} data-testid="confirm-beneficiaries-btn">
                    {saving ? 'Validation...' : 'Confirmer définitivement'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Security */}
            <Card className="bg-card/80 backdrop-blur">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Sécurité
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Lock className="w-4 h-4 mr-2" />
                      Modifier le mot de passe
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Changer le mot de passe</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Mot de passe actuel</Label>
                        <div className="relative">
                          <Input 
                            type={showPasswords ? 'text' : 'password'}
                            value={passwordData.current_password}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                          />
                          <button 
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                            onClick={() => setShowPasswords(!showPasswords)}
                          >
                            {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Nouveau mot de passe</Label>
                        <Input 
                          type={showPasswords ? 'text' : 'password'}
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Confirmer le mot de passe</Label>
                        <Input 
                          type={showPasswords ? 'text' : 'password'}
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        />
                      </div>
                      <Button onClick={handlePasswordChange} disabled={saving} className="w-full" style={{ backgroundColor: primaryColor }}>
                        {saving ? 'Modification...' : 'Modifier'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Lock className="w-4 h-4 mr-2" />
                      Modifier le code PIN
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Changer le code PIN</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Code PIN actuel</Label>
                        <Input 
                          type="password"
                          maxLength={6}
                          value={pinData.current_pin}
                          onChange={(e) => setPinData(prev => ({ ...prev, current_pin: e.target.value.replace(/\D/g, '') }))}
                          placeholder="******"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nouveau code PIN (6 chiffres)</Label>
                        <Input 
                          type="password"
                          maxLength={6}
                          value={pinData.new_pin}
                          onChange={(e) => setPinData(prev => ({ ...prev, new_pin: e.target.value.replace(/\D/g, '') }))}
                          placeholder="******"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Confirmer le code PIN</Label>
                        <Input 
                          type="password"
                          maxLength={6}
                          value={pinData.confirm_pin}
                          onChange={(e) => setPinData(prev => ({ ...prev, confirm_pin: e.target.value.replace(/\D/g, '') }))}
                          placeholder="******"
                        />
                      </div>
                      <Button onClick={handlePinChange} disabled={saving} className="w-full" style={{ backgroundColor: primaryColor }}>
                        {saving ? 'Modification...' : 'Modifier'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* KYC Documents */}
        <Card className="bg-card/80 backdrop-blur">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documents KYC
                </CardTitle>
                <CardDescription>Televersez vos justificatifs pour verification</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {availableKycTypes.length === 0 ? (
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="font-medium text-green-500">Aucun document requis</p>
                <p className="text-sm text-muted-foreground">Votre conseiller n'a demandé aucun document pour le moment</p>
              </div>
            ) : (
            <>
            {(() => {
              const allApproved = availableKycTypes.every(t => 
                kycDocs.some(d => d.document_type === t.value && d.status === 'approved')
              );
              if (allApproved) {
                return (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="font-medium text-green-500">Profil vérifié</p>
                    <p className="text-sm text-muted-foreground">Tous vos documents ont ete approuves</p>
                  </div>
                );
              }
              return null;
            })()}

            {/* Document by document status */}
            <div className="space-y-3">
              {(() => {
                // Group identity_front and identity_back together
                const identityTypes = availableKycTypes.filter(t => t.group === 'identity' || t.value === 'identity_front' || t.value === 'identity_back');
                const otherTypes = availableKycTypes.filter(t => t.group !== 'identity' && t.value !== 'identity_front' && t.value !== 'identity_back');
                const elements = [];

                // Render identity group as a single card with recto/verso
                if (identityTypes.length > 0) {
                  const frontUploaded = kycDocs.find(d => d.document_type === 'identity_front');
                  const backUploaded = kycDocs.find(d => d.document_type === 'identity_back');
                  const bothApproved = frontUploaded?.status === 'approved' && backUploaded?.status === 'approved';
                  const anyRejected = frontUploaded?.status === 'rejected' || backUploaded?.status === 'rejected';

                  elements.push(
                    <div key="identity-group" className={`p-4 rounded-lg border ${bothApproved ? 'border-green-500/30 bg-green-500/5' : anyRejected ? 'border-red-500/30 bg-red-500/5' : 'border-border'}`} data-testid="kyc-profile-identity-group">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bothApproved ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                          <FileText className={`w-4 h-4 ${bothApproved ? 'text-green-500' : 'text-blue-500'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Pièce d'identité (CNI / Passeport)</p>
                          <p className="text-xs text-muted-foreground">Recto et verso requis</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[{ type: identityTypes.find(t => t.value === 'identity_front'), uploaded: frontUploaded, label: 'Recto', testId: 'kyc-profile-identity-front' },
                          { type: identityTypes.find(t => t.value === 'identity_back'), uploaded: backUploaded, label: 'Verso', testId: 'kyc-profile-identity-back' }]
                          .filter(item => item.type)
                          .map(({ type, uploaded, label, testId }) => {
                            const isApproved = uploaded?.status === 'approved';
                            const isRejected = uploaded?.status === 'rejected';
                            const isPending = uploaded && !isApproved && !isRejected;
                            const isMissing = !uploaded;
                            return (
                              <div key={type.value} className={`p-3 rounded-lg border ${isApproved ? 'border-green-500/30 bg-green-500/5' : isRejected ? 'border-red-500/30 bg-red-500/5' : isPending ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-dashed border-border'}`} data-testid={testId}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium">{label}</span>
                                  {isApproved && <Badge className="bg-green-500/20 text-green-500 border-green-500/50 text-[10px]">Approuvé</Badge>}
                                  {isPending && <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/50 text-[10px]">En attente</Badge>}
                                  {isRejected && <Badge className="bg-red-500/20 text-red-500 border-red-500/50 text-[10px]">Refusé</Badge>}
                                </div>
                                {uploaded && <p className="text-xs text-muted-foreground mb-2 truncate">{uploaded.file_name}</p>}
                                {isRejected && uploaded.rejection_reason && <p className="text-xs text-red-500 mb-2">{uploaded.rejection_reason}</p>}
                                {(isMissing || isRejected) && (
                                  <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="text-xs h-8"
                                    disabled={uploadingKyc}
                                    data-testid={`${testId}-upload`}
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      e.target.value = '';
                                      try {
                                        setUploadingKyc(true);
                                        const reader = new FileReader();
                                        reader.onloadend = async () => {
                                          await axios.post(`${API}/kyc/upload`, {
                                            document_type: type.value,
                                            file_url: reader.result,
                                            file_name: file.name
                                          }, { headers: { Authorization: `Bearer ${token}` } });
                                          toast.success(`${label} téléversé`);
                                          fetchData();
                                          setUploadingKyc(false);
                                        };
                                        reader.readAsDataURL(file);
                                      } catch {
                                        toast.error('Erreur téléversément');
                                        setUploadingKyc(false);
                                      }
                                    }}
                                  />
                                )}
                                {isMissing && !isRejected && <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG</p>}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                }

                // Render other types normally
                otherTypes.forEach(docType => {
                  const uploaded = kycDocs.find(d => d.document_type === docType.value);
                  const isApproved = uploaded?.status === 'approved';
                  const isRejected = uploaded?.status === 'rejected';
                  const isPending = uploaded && !isApproved && !isRejected;
                  const isMissing = !uploaded;

                  elements.push(
                    <div key={docType.value} className={`p-4 rounded-lg border ${isApproved ? 'border-green-500/30 bg-green-500/5' : isRejected ? 'border-red-500/30 bg-red-500/5' : isPending ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-dashed border-border'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isApproved ? 'bg-green-500/20' : isRejected ? 'bg-red-500/20' : isPending ? 'bg-yellow-500/20' : 'bg-muted'}`}>
                            {isApproved ? <CheckCircle2 className="w-4 h-4 text-green-500" /> :
                             isRejected ? <XCircle className="w-4 h-4 text-red-500" /> :
                             isPending ? <Clock className="w-4 h-4 text-yellow-500" /> :
                             <FileText className="w-4 h-4 text-muted-foreground" />}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{docType.label}</p>
                            {uploaded && <p className="text-xs text-muted-foreground">{uploaded.file_name} — {new Date(uploaded.uploaded_at).toLocaleDateString('fr-FR')}</p>}
                            {isMissing && <p className="text-xs text-muted-foreground">Document non fourni</p>}
                            {isRejected && uploaded.rejection_reason && <p className="text-xs text-red-500 mt-0.5">{uploaded.rejection_reason}</p>}
                          </div>
                        </div>
                        <div>
                          {isApproved && <Badge className="bg-green-500/20 text-green-500 border-green-500/50 text-xs">Approuvé</Badge>}
                          {isPending && <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/50 text-xs">En attente</Badge>}
                          {isRejected && <Badge className="bg-red-500/20 text-red-500 border-red-500/50 text-xs">Refusé</Badge>}
                        </div>
                      </div>
                      {(isMissing || isRejected) && (
                        <div className="mt-3 flex items-center gap-2">
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="flex-1 text-xs h-8"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              e.target.value = '';
                              try {
                                setUploadingKyc(true);
                                const reader = new FileReader();
                                reader.onloadend = async () => {
                                  await axios.post(`${API}/kyc/upload`, {
                                    document_type: docType.value,
                                    file_url: reader.result,
                                    file_name: file.name
                                  }, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success(`${docType.label} téléversé`);
                                  fetchData();
                                  setUploadingKyc(false);
                                };
                                reader.readAsDataURL(file);
                              } catch {
                                toast.error('Erreur lors du téléversément');
                                setUploadingKyc(false);
                              }
                            }}
                          />
                          <span className="text-xs text-muted-foreground whitespace-nowrap">PDF, JPG, PNG</span>
                        </div>
                      )}
                    </div>
                  );
                });

                return elements;
              })()}
            </div>
            </>
            )}
          </CardContent>
        </Card>

        {/* Analyst Contact */}
        {profile?.analyst && (
          <Card className="bg-card/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Votre conseiller</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6" style={{ color: primaryColor }} />
                </div>
                <div>
                  <p className="font-semibold">{profile.analyst.first_name} {profile.analyst.last_name}</p>
                  <p className="text-sm text-muted-foreground">{profile.analyst.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
